import { useEffect, useRef, useState } from 'react';
import { formatDuration, parseDuration } from '../lib/duration';

/**
 * Accepts the shapes people type (1:30, 1.5, 90m, 1h30) and normalises to
 * hh:mm on blur. Reports minutes upward, or null while the text is unusable.
 */
export function DurationInput({
  minutes,
  onChange,
  className = '',
  placeholder = 'hh:mm',
  ...rest
}: {
  minutes: number | null;
  onChange: (minutes: number | null) => void;
  className?: string;
  placeholder?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>) {
  const [text, setText] = useState(minutes != null ? formatDuration(minutes) : '');

  // The last value this input reported upward. Anything else arriving in the
  // prop came from outside and should replace what is on screen.
  const emitted = useRef<number | null>(minutes);

  useEffect(() => {
    // Never reformat the value we just emitted: mid-word that would rewrite
    // "00:4" to "00:04", pushing the next keystroke into "00:045".
    if (minutes === emitted.current) return;
    emitted.current = minutes;
    setText(minutes != null ? formatDuration(minutes) : '');
  }, [minutes]);

  const invalid = text.trim() !== '' && parseDuration(text) === null;

  return (
    <input
      {...rest}
      value={text}
      inputMode="text"
      placeholder={placeholder}
      aria-invalid={invalid}
      title="Accepts 1:30, 1.5, 90m or 1h30"
      onChange={(e) => {
        setText(e.target.value);
        const parsed = parseDuration(e.target.value);
        emitted.current = parsed;
        onChange(parsed);
      }}
      onBlur={(e) => {
        // Tidy to hh:mm only once the field is done being typed into.
        const parsed = parseDuration(e.target.value);
        if (parsed !== null) setText(formatDuration(parsed));
      }}
      className={`field nums text-right ${invalid ? '!border-[var(--color-coral-400)]' : ''} ${className}`}
    />
  );
}
